/** @deprecated Visibilidade agora deriva do projeto. Mantido apenas como tipo legado em respostas existentes. */
export type SocialPostVisibility = 'Public' | 'Private' | 'FollowersOnly';
export type SocialMediaType = 'Image' | 'Video';
/** `User` = denúncia da conta (também usada no menu do perfil de projeto, contra o dono). */
export type SocialReportTargetType = 'Post' | 'Comment' | 'User';
/** SuspendUser/BanUser sancionam o autor/conta SÓ no feed social (o resto da plataforma segue). */
export type SocialModerationDecision = 'ApproveContent' | 'RemoveContent' | 'WarnUser' | 'Dismiss' | 'SuspendUser' | 'BanUser';
/** Sanção atual de um usuário no feed social. */
export type SocialSanctionStatus = 'None' | 'Suspended' | 'Banned';
export type SocialPostStatus = 'Active' | 'Archived' | 'Deleted';
/** Filtro de status da fila de moderação. Em minúsculas para casar 1:1 com o
 *  query param `?status=` e com as chaves de `ModerationCounts`. */
export type SocialReportStatus = 'pending' | 'reviewed' | 'dismissed';

export interface SocialMediaRequestItem {
  mediaType: SocialMediaType;
  url: string;
  thumbnailUrl?: string | null;
  sortOrder: number;
}

export interface SocialCreatePostRequest {
  caption?: string | null;
  projectId: string;
  /** @deprecated mantido apenas no período de transição; backend ignora. */
  visibility?: SocialPostVisibility | null;
  mediaIds: string[];
  taggedUserIds?: string[] | null;
}

export interface SocialUpdatePostRequest {
  caption?: string | null;
  mediaIds?: string[] | null;
  taggedUserIds?: string[] | null;
}

export interface SocialCommentRequest {
  body: string;
}

export interface SocialReportRequest {
  targetType: SocialReportTargetType;
  targetId: string;
  reason: string;
  /** Chave estruturada do motivo (spam, offensive, ...) — usada para filtrar na curadoria. */
  reasonKey?: string;
}

export interface SocialModerationDecisionRequest {
  decision: SocialModerationDecision;
  /** Obrigatório quando `decision === 'RemoveContent'` (vira o corpo da notificação ao autor). */
  reason?: string | null;
  notes?: string | null;
  /** Prazo da suspensão em dias (7, 15 ou 30). Obrigatório quando `decision === 'SuspendUser'`. */
  suspensionDays?: number | null;
}

/** Contagem de denúncias por status — alimenta os badges das abas. */
export interface ModerationCounts {
  pending: number;
  reviewed: number;
  dismissed: number;
  /** Contas suspensas / banidas do feed (abas de sanção do admin). */
  suspended?: number;
  banned?: number;
}

/** Conta sancionada no feed (GET admin/social/sanctions). */
export interface SanctionedUser {
  userId: string;
  nickname?: string | null;
  name?: string | null;
  sanction: SocialSanctionStatus | string;
  sanctionedAt?: string | null;
  /** Fim da suspensão (UTC); null em banimento (sem prazo). */
  sanctionUntil?: string | null;
  sanctionReason?: string | null;
  postsCount: number;
}

export interface SanctionedUsersResponse {
  items: SanctionedUser[];
  total: number;
}

/** Mídia resolvida no preview da fila de moderação (post). */
export interface ModerationMediaItem {
  url: string;
  mediaType?: SocialMediaType | string;
  thumbnailUrl?: string | null;
  sortOrder?: number;
}

/** Conteúdo de post resolvido para o card da fila. */
export interface ModerationQueuePost {
  postId: string;
  authorUserId: string;
  authorNickname?: string | null;
  authorName?: string | null;
  caption?: string | null;
  status: SocialPostStatus | string;
  hiddenByModeration: boolean;
  media: ModerationMediaItem[];
}

/** Conteúdo de comentário resolvido para o card da fila. */
export interface ModerationQueueComment {
  commentId: string;
  authorUserId: string;
  authorNickname?: string | null;
  authorName?: string | null;
  body: string;
  parentPostId: string;
  parentPostSummary?: string | null;
  deleted: boolean;
}

/** Conta denunciada resolvida para o card da fila. */
export interface ModerationQueueUser {
  userId: string;
  nickname?: string | null;
  name?: string | null;
  sanction: SocialSanctionStatus | string;
  postsCount: number;
}

/**
 * Item da fila de moderação, agrupado por alvo `(targetType, targetId)`.
 * Discriminado por `targetType`: quando `Post`, vem `post`; quando `Comment`, vem `comment`;
 * quando `User`, vem `user`. Uma decisão sobre `representativeReportId` resolve todas as
 * denúncias pendentes do alvo.
 */
export interface ModerationQueueItem {
  targetType: SocialReportTargetType;
  targetId: string;
  reportCount: number;
  reasons: string[];
  /** Chaves estruturadas dos motivos presentes no grupo (para o filtro por tipo). */
  reasonKeys?: string[];
  reporterUserIds: string[];
  firstReportedAt: string;
  latestReportedAt: string;
  representativeReportId: string;
  /** Conteúdo já removido por moderação. */
  contentRemoved: boolean;
  /** Alvo não existe mais (ex.: apagado pelo autor). */
  contentMissing: boolean;
  post?: ModerationQueuePost | null;
  comment?: ModerationQueueComment | null;
  user?: ModerationQueueUser | null;
}

export interface SocialPostMediaResponse {
  id: string;
  mediaType: SocialMediaType;
  url: string;
  thumbnailUrl?: string | null;
  sortOrder: number;
}

export interface SocialPostResponse {
  id: string;
  authorUserId: string;
  authorNickname?: string | null;
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  /** Projeto que publicou. Pode ser null em posts legados durante transição. */
  projectId: string | null;
  projectName: string | null;
  projectAvatarUrl: string | null;
  /** O viewer já segue o projeto do post: decide o botão "Seguir" do card sem lista prévia. */
  viewerFollowsProject?: boolean;
  isLikedByViewer: boolean;
  isSponsored: boolean;
  caption?: string | null;
  /** @deprecated Mantido pelo backend como legacy; ignorar em clientes novos. */
  visibility?: SocialPostVisibility | string;
  status: SocialPostStatus;
  archivedAt?: string | null;
  createdAt: string;
  likesCount: number;
  commentsCount: number;
  hiddenByModeration: boolean;
  media: SocialPostMediaResponse[];
  taggedUserIds: string[];
}

export interface SocialFeedResponse {
  items: SocialPostResponse[];
  nextCursor?: string | null;
}

export interface SocialCommentResponse {
  id: string;
  postId: string;
  /** null em comentário top-level; GUID do pai quando é resposta. Replies são sempre 1 nível (achatadas pelo backend). */
  parentCommentId?: string | null;
  authorUserId: string;
  authorNickname?: string | null;
  authorName?: string | null;
  body: string;
  createdAt: string;
  modifiedAt: string;
}

/**
 * Vitrine simplificada do usuário. Não contém mais posts/seguidores
 * (esses conceitos passaram para o projeto).
 */
export interface SocialProfileResponse {
  userId: string;
  name?: string | null;
  nickname?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  website?: string | null;
  projectsCount: number;
  projectIdsPreview: string[];
  badgesCount: number;
  marketplaceItemsCount: number;
}

export interface UpdateProfileRequest {
  bio?: string | null;
  website?: string | null;
  avatarMediaId?: string | null;
  nickname?: string | null;
}

export interface AvatarUploadResult {
  mediaId: string;
  objectKey: string;
  fileName: string;
}

export interface SocialMediaUploadRequest {
  file: string;
}

export interface SocialMediaUploadResponse {
  mediaId: string;
  objectKey: string;
  fileName: string;
}

export interface SocialFollowUserInfo {
  userId: string;
  nickname?: string | null;
  name?: string | null;
  isFollowedByViewer?: boolean;
}

/** Resultado de `GET user/social/users/search` (inclui flag de follow para o viewer). */
export interface SocialSearchUserHit extends SocialFollowUserInfo {
  isFollowedByViewer?: boolean;
}

export interface SocialFollowListResponse {
  users: SocialFollowUserInfo[];
  total: number;
}

export interface SocialReportResponse {
  reportId: string;
  status: string;
}

// ===== Projeto-cêntrico (feed unificado) =====

/** VCU resumida exibida na sidebar do perfil do projeto. */
export interface VcuSummaryResponse {
  vcuId: string;
  nickname: string | null;
  serialNumber: string | null;
  familyId: number | null;
  /** true se VCU está em uso ativo no projeto; false = histórico (já desvinculada). */
  active: boolean;
}

/** Perfil completo do projeto (header + sidebar). */
export interface ProjectProfileResponse {
  projectId: string;
  projectName: string;
  ownerUserId: string;
  ownerNickname: string | null;
  ownerName: string | null;
  ownerAvatarUrl: string | null;
  avatarUrl: string | null;
  bio: string | null;
  vehicleManufacturer: string | null;
  vehicleModel: string | null;
  vehicleYear: number | null;
  followersCount: number;
  postsCount: number;
  isFollowedByViewer: boolean;
  isOwnedByViewer: boolean;
  /** Atributos do projeto (manufacturer, vehicle-model, date-of-manufacture, detail, etc.) — exclui o bulk `photo`. */
  attributes: Record<string, string>;
  /** VCUs vinculadas ao projeto (ativas e históricas). */
  vcus: VcuSummaryResponse[];
  /** 1 = Público (listado), 2 = Somente com link, 3 = Privado. */
  sharingProjectTypeId?: number;
}

/** Item retornado pela busca de projetos no autocomplete agrupado. */
export interface ProjectSearchHitResponse {
  projectId: string;
  projectName: string;
  ownerUserId: string;
  ownerNickname: string | null;
  primaryImageUrl: string | null;
  vehicleManufacturer: string | null;
  vehicleModel: string | null;
}

/** Página de `GET me/followed-projects/paged`: projetos que o viewer segue, mais recentes primeiro. */
export interface FollowedProjectsPageResponse {
  items: ProjectSearchHitResponse[];
  total: number;
}

/** Resumo mínimo dos projetos públicos do viewer (seletor "publicar em qual"). */
export interface ProjectMiniResponse {
  projectId: string;
  projectName: string;
  primaryImageUrl: string | null;
}
